import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { LoadingService } from '@core/services/loading.service';
import { UpdateApprovalService } from '@core/services/update-approvals';
import { Store } from '@ngxs/store';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Observable, BehaviorSubject, Subscription, take } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';
@Component({
  selector: 'app-update-approvals-table',
  standalone: true,
  imports: [
    FiltersComponent,
    TableSkeletonLoaderComponent,
    TableNavigatorComponent,
    CommonModule,
    PermissionDirective,
  ],
  templateUrl: './update-approvals-table.component.html',
  styleUrl: './update-approvals-table.component.scss',
  providers: [TitleCasePipe]
})
export class UpdateApprovalsTableComponent {
  @Input() tableType: 'global' | 'individual' = 'individual';

  isFetchingApprovalList$!: Observable<boolean>;
  approvalListSubject = new BehaviorSubject<any>(null);
  totalCountSubject = new BehaviorSubject<number>(0);
  isFilterActive: boolean = false;
  hadFilterBefore: boolean = false;
  accountId!: string;
  updateId!: string;
  subs: Subscription[] = [];

  approvalList$ = this.approvalListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();

  router = inject(Router);
  store = inject(Store);
  activatedRoute = inject(ActivatedRoute);
  updateApprovalService = inject(UpdateApprovalService);
  loadingService = inject(LoadingService);

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },

    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'pending', value: 'Pending' },
        { key: 'approved', value: 'Approved' },
        { key: 'rejected', value: 'Rejected' },
      ],
    },
  ];

  constructor(private titleCasePipe: TitleCasePipe) {}

  ngOnInit() {
    this.accountId = this.activatedRoute.snapshot.parent?.params['accountId'];
    this.updateId = this.activatedRoute.snapshot.queryParams['updateId']
    console.log(this.updateId)
    this.getDataByTableType();
    this.filterEventListener();
    this.loadApprovalList();
  }

  filterEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        query = `start_date=${params['start_date']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `status=${params['status']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterApprovalList({ filter: query })
        );
      } else {
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'updateApprovals',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  loadApprovalList(page = 1) {
    if (!this.updateId) {
      const sub = this.approvalList$.pipe(take(1)).subscribe((approval) => {
        const hasData = Array.isArray(approval) && approval.length > 0;
        // only dispatch if store is empty
        if (!hasData) {
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'updateApprovals',
              params: new Pagination({
                skip: (page - 1) * 10,
                size: 10,
              }),
            })
          );
        }
      });
      this.subs.push(sub);
    } else {
      const currentPage = this.activatedRoute.snapshot.queryParams['page'];
      const rawPage = currentPage ? parseInt(currentPage) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;
      const query = `skip=${skip}&take=${take}&search_text=${this.accountId}`;
      const sub = this.updateApprovalService
        .getApprovalList(undefined, query)
        .subscribe({
          next: (response) => {
            this.approvalListSubject.next(response.data.data);
            this.totalCountSubject.next(response.data.total_count);
          },
          error: (error) => {
            console.error('Failed to fetch approval list', error);
          },
        });
      this.subs.push(sub);
    }
  }

  getDataByTableType() {
    if (!this.updateId) {
      this.approvalList$ = this.store.select(
        TableSelectors.getTableData('updateApprovals')
      );
      this.totalCount$ = this.store.select(
        TableSelectors.getTableTotalCount('updateApprovals')
      );
      this.isFetchingApprovalList$ = this.store.select(
        TableSelectors.getTableLoading('updateApprovals')
      );
    } else {
      console.log("vnvnvn")
      this.approvalList$ = this.approvalListSubject.asObservable();
      this.totalCount$ = this.totalCountSubject.asObservable();
      this.isFetchingApprovalList$ = this.loadingService.getLoadingObservable(
        'get-update-approvals'
      );
    }
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }

  viewUpdateDetails(approval: {
    account_id: string;
    combined_id: string;
    account_type: string;
    joint_account_id: string;
    edited_by: string;
  }) {
    let accountId = approval.account_id
      ? approval.account_id
      : approval.joint_account_id;
    this.router.navigateByUrl(
      `/admin/accounts/${approval.account_type}/edit/${accountId}/update-approval?updateId=${approval.combined_id}&editedBy=${encodeURIComponent(approval.edited_by)}`
    );
  }

  formatAccountName(accountName: string): string {
    if (!accountName || accountName === '-') {
      return accountName;
    }
    
    // Split by ' & ' to handle joint accounts
    const names = accountName.split(' & ');
    
    return names.map(name => {
      // Remove [null] strings and extra spaces cause backend someimes returns [null] as middle name
      const cleanedName = name.replace(/\[null\]/gi, '').replace(/\s+/g, ' ').trim();
      
      if (!cleanedName) return '';
      
      const parts = cleanedName.split(' ');
      if (parts.length === 0) return cleanedName;
      
      // Backend order: lastName, firstName, middleName
      // We want: firstName middleName LASTNAME
      if (parts.length === 1) {
        // Only last name
        return parts[0].toLocaleUpperCase();
      } else if (parts.length === 2) {
        // lastName and firstName only
        const lastName = parts[0].toLocaleUpperCase();
        const firstName = this.titleCasePipe.transform(parts[1]);
        return `${firstName} ${lastName}`;
      } else {
        // lastName firstName middleName -> firstName middleName LASTNAME
        const lastName = parts[0].toLocaleUpperCase();
        const firstName = this.titleCasePipe.transform(parts[1]);
        const middleName = this.titleCasePipe.transform(parts[2]);
        return `${firstName} ${middleName} ${lastName}`;
      }
    }).join(' & ');
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
